import { FC, useMemo } from 'react'
import cn from 'classnames'
import * as moment from 'moment'
import styles from 'components/features/pfm/SubscriptionGeneralSection/SubscriptionGeneralSection.styl'
import { Subscription } from 'interfaces/subscription'
import Badge, { BadgeTheme } from 'components/features/insights/Insight/Badge'
import { createPluralFormatter } from 'helpers/formatHelpers'
import TextButton from 'components/atoms/buttons/TextButton'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { cancelSubscriptionConfirmActionCreator } from 'actions/subscriptionActions'
import Button from 'components/features/pfm/Button/Button'
import ChevronSvg from 'icons/chevron.svg'
import SvgIcon from 'components/atoms/SvgIcon'
import { openSubscriptionPageActionCreator } from 'actions/floatingPanesActions'
import { currentCardSelector } from 'components/features/creditCard/creditCardStore/selectors'

export const getDiffDaysCopy = (nextBillingDate: string, now: number) => {
  const diffDays = Math.floor(
    moment.duration(
      +moment(nextBillingDate) - +moment(now).startOf('day')
    ).asDays()
  )

  if (diffDays <= 0) {
    return `Almost expired`
  }

  return `${ diffDays } ${ pluralizeDays(Number(diffDays)) } left`
}

const pluralizeDays = createPluralFormatter('day', 'days')

type SubscriptionGeneralSectionProps = Subscription & {
  isSubscriptionCanBeCancelled: boolean
}

export const SubscriptionGeneralSection: FC<SubscriptionGeneralSectionProps> = (props) => {
  const { type, provider, stripe, native, next_billing_date, isSubscriptionCanBeCancelled } = props

  const stripeAction = stripe ? stripe.action : null
  const isElapsedPremium = Boolean(!type && provider === 'stripe' && stripeAction)
  const isNativeSubscription = type && provider === 'native'
  const isBasic = !type && !stripeAction
  const isTrial = isNativeSubscription && native?.type === 'trial'
  const isFreePremium = isNativeSubscription && (native?.type === 'free_premium' || native?.type === 'partnered')

  const badgeChild = useMemo(() => {
    let badgeTheme: BadgeTheme = 'gray'
    if (type === 'premium' || isElapsedPremium) {
      badgeTheme = 'golden'
    }

    const badgeCopy = badgeTheme === 'gray' ? 'Basic' : 'Premium'

    const wrapperClassName = cn(styles.badgeWrapper, {
      [styles.blur]: isElapsedPremium,
    })

    return (
      <div className={ wrapperClassName }>
        <Badge theme={ badgeTheme }>
          { badgeCopy }
        </Badge>
      </div>
    )

  }, [type, isElapsedPremium])

  const daysLeftChild = useMemo(() => {
    if (isTrial && next_billing_date) {
      return (
        <div className={ styles.daysLeft }>
          { getDiffDaysCopy(next_billing_date, Date.now()) }
        </div>
      )
    }

    return null
  }, [isTrial, next_billing_date])

  const activeCard = useStoreSelector(currentCardSelector)
  const dispatch = useStoreDispatch()
  const secondaryCtaChild = useMemo(() => {
    if (isSubscriptionCanBeCancelled && !activeCard) {
      const handleClick = () => dispatch(cancelSubscriptionConfirmActionCreator.request({}))

      return (
        <div className={ styles.secondaryCtaWrapper }>
          <TextButton size='large' onClick={ handleClick }>Downgrade</TextButton>
        </div>
      )
    }

    return null
  }, [activeCard, dispatch, isSubscriptionCanBeCancelled])

  const mainCopyLineChild = useMemo(() => {
    let copy: string | null = null
    if (isBasic) {
      copy = 'Take your Status to the next level! Go Premium to unlock more features and rewards.'
    }
    if (isFreePremium) {
      copy = 'Your Premium membership is free. Enjoy!'
    }
    if (activeCard) {
      copy = 'Thanks for being a Status Card holder. Your Premium membership is free. Enjoy!'
    }
    if (isTrial) {
      copy = 'Thanks for trying Premium!'
    }

    if (!copy) {
      return null
    }

    return (
      <div className={ styles.mainCopyLine }>{ copy }</div>
    )
  }, [activeCard, isBasic, isFreePremium, isTrial])

  const mainCtaLineChild = useMemo(() => {
    if (!isBasic) {
      return null
    }

    const chevronChild = (
      <div className={ styles.mainCtaChevron }>
        <SvgIcon src={ ChevronSvg } />
      </div>
    )

    const handleClick = () => dispatch(openSubscriptionPageActionCreator())

    return (
      <div className={ styles.mainCtaLine }>
        <Button theme='golden' fullWidth size='responsive' shadow='light' onClick={ handleClick }>
          <div className={ styles.mainCtaInnerWrapper }>
            <span className={ styles.mainCtaCopy }>
              Explore Premium Features
            </span>
            { chevronChild }
          </div>
        </Button>
      </div>
    )
  }, [dispatch, isBasic])

  return (
    <div className={ styles.subscriptionGeneralSection }>
      <div className={ styles.badgeLine }>
        { badgeChild }
        { daysLeftChild }
        { secondaryCtaChild }
      </div>
      { mainCopyLineChild }
      { mainCtaLineChild }
    </div>
  )
}
